import { Injectable, PipeTransform } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Article } from 'src/app/shared/models/article.interface';
import { debounceTime, delay, switchMap, tap, map } from 'rxjs/operators';
import { SortColumn, SortDirection } from './sortable.directive';
import { DecimalPipe } from '@angular/common';
import { Endpoints, ArticlePushObject, ArticleVerifyObject } from './article.models';
import { DeployModel } from 'src/app/shared/models/DeployModel';

interface State {
	searchTerm: string;
	sortColumn: SortColumn;
	sortDirection: SortDirection;
}
interface SystemArticles{
  system: string;
  articles: Article[];
  showTable: boolean;
}

interface SearchResult {
	articles: Article[];
}

/**
 * Sorts the table by the specified column.
 * @param arr array to sort
 * @param column column to sort by
 * @param sortDirection direction to sort by
 * @returns sorted array
 */
function sort(arr: Article[], column: SortColumn, sortDirection: SortDirection): Article[] {
  const useColumn = column || 'ArticleName';
  const useDirection = sortDirection || 'asc';
  const sorted = [...arr].sort()
  .map((arr, i) => [arr[useColumn], i] as const)
  .sort()
  .map((([, i]) => arr[i]));

  if (useDirection === 'desc' || '') {
    return sorted;
  } else {
    return sorted.reverse();
  }
}

/**
 * Filters the table by the specified term.
 * @param article article to filter
 * @param term term to filter by
 * @param pipe pipe to transform the article
 * @returns boolean if the article matches the term
 */
function matches(article: Article, term: string, pipe: PipeTransform) {
  return (
    article.ArticleName?.includes(term) ||
    article.systemName?.toLowerCase().includes(term.toLowerCase()) ||
    article.InsideW == parseInt(term)  ||
    article.OutsideW == parseInt(term) ||
    article.Depth == parseInt(term)
  );
}

@Injectable({
  providedIn: 'root'
})
export class ArticleService {
  private _loading$ = new BehaviorSubject<boolean>(true);
	private _search$ = new Subject<void>();
  private _articles$: BehaviorSubject<Article[]> = new BehaviorSubject<Article[]>([]);
  private _articlesFromDb: Article[] = [];
  private _systems$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  /**
    * Use the first apiUrl for dev API access and the second for development with local backend.
    */
  apiUrl = environment.apiUrl;
  // apiUrl = environment.localUrl;

  private _state: State = {
		searchTerm: '',
		sortColumn: '',
		sortDirection: '',
	};

  constructor(
    private http: HttpClient,
    private pipe: DecimalPipe
  ) {
   // this.loadArticles();
    this._search$
			.pipe(
				tap(() => this._loading$.next(true)),
				debounceTime(200),
				switchMap(() => this._search()),
				delay(200),
				tap(() => this._loading$.next(false)),
			)
			.subscribe((result) => {
				this._articles$.next(result.articles);
			});

		this._search$.next();
  }

  /**
   * Loads the articles from the database
   */
  // private loadArticles(): void {
  //   this.http.get<Article[]>(`${this.apiUrl}/api/DXFReader/GetAllArticleSectionalProperties`).subscribe({
  //     next: (articles: Article[]) => {
  //       this._articlesFromDb = articles;
  //       this._articles$.next(articles);
  //     },
  //     error: (error) => {
  //       console.error('Error fetching articles:', error);
  //     }
  //   });
  // }

  getSystemArticleMap1(): Observable<{ system: string, articles: Article[], showTable: boolean }[]> {
    return this.articles$.pipe(
      map((articles: Article[]) => {
        const systemArticleMap = new Map<string, Article[]>();
        articles.forEach((article: Article) => {
          const systemName = article.systemName;
          if (systemName !== undefined) {
            if (!systemArticleMap.has(systemName)) {
              systemArticleMap.set(systemName, []);
            }
            systemArticleMap.get(systemName)?.push(article);
          }
        });
        return Array.from(systemArticleMap.entries()).map(([system, articles]) => ({ system, articles, showTable: false }));
      })
    );
  }

  // getSystemArticleMap(): Observable<{ application: string, systemArticles: SystemArticles[], showApplicationTable: boolean }[]> {
  //   return this.articles$.pipe(
  //     map((articles: Article[]) => {

  //       const applicationSystemArticleMap = new Map<string, Map<string, Article[]>>();
  //       articles.forEach((article: Article) => {
  //         const application = article.application;
  //         const systemName = article.systemName;
          
  //         if (application !== undefined && systemName !== undefined) {
  //           if (!applicationSystemArticleMap.has(application)) {
  //             applicationSystemArticleMap.set(application, new Map());
  //           }
  //           var applicationValue = applicationSystemArticleMap.get(application);
  //           if(applicationValue == undefined || applicationValue.size == 0){
  //             applicationValue = new Map<string, Article[]>();
  //             applicationValue.set(systemName, []);
  //           }
            
  //           if (!applicationValue.has(systemName)) {
  //             applicationValue.set(systemName, []);
  //           }
  //           applicationValue.get(systemName)?.push(article);
          
  //           applicationSystemArticleMap.set(application, applicationValue);
  //         }

  //       });
  //       return Array.from(applicationSystemArticleMap.entries()).map(([application, systemArticles]) => ({ application, systemArticles : this.getArrayFromSystemArticlesMap(systemArticles), showApplicationTable: false }));
  //     })
  //   );
  // }

  getArrayFromSystemArticlesMap(systemArticleMap: Map<string, Article[]>){
    return Array.from(systemArticleMap.entries()).map(([system, articles]) => ({ system, articles, showTable: false }));
  }

  getArticlesBySystem(systemApiModel: any): Observable<Article[]> {
    // const trimmedSystemName = systemName.trim();  // Remove leading/trailing whitespace
    // const encodedSystemName = encodeURIComponent(trimmedSystemName);
    return this.http.post<Article[]>(`${this.apiUrl}/api/Article/GetArticlesBySystemName`, systemApiModel);
  }

  getSystemArticleMap(): Observable<any> {
    // Modify this method to return only system and application names initially
    return this.http.get<any>(`${this.apiUrl}/api/Search/GetAllApplicationAndSystemTypes`);
  }
  
  // getSystemArticleMap(): Observable<Map<string, Article[]>> {
  //   return this.articles$.pipe(
  //     map((articles: Article[]) => {
  //       const systemArticleMap = new Map<string, Article[]>();
  //       articles.forEach((article: Article) => {
  //         const systemName = article.systemName;
  //         if (systemName !== undefined) {
  //           if (!systemArticleMap.has(systemName)) {
  //             systemArticleMap.set(systemName, []);
  //           }
  //           systemArticleMap.get(systemName)?.push(article);
  //         }
  //       });
  //       return systemArticleMap;
  //     })
  //   );
  // }

  public convertToMap(articles: Article[]): Map<string, Article[]> {
    const systemArticleMap = new Map<string, Article[]>();
    articles.forEach((article: Article) => {
      const systemName = article.systemName;
      if (systemName !== undefined) {
        if (!systemArticleMap.has(systemName)) {
          systemArticleMap.set(systemName, []);
        }
        systemArticleMap.get(systemName)?.push(article);
      }
    });
    return systemArticleMap;
  }

  get articles$(): Observable<Article[]> {
    return this._articles$.asObservable();
  }
	get loading$() {
		return this._loading$.asObservable();
	}
	get searchTerm() {
		return this._state.searchTerm;
	}

	set searchTerm(searchTerm: string) {
		this._set({ searchTerm });
	}
	set sortColumn(sortColumn: SortColumn) {
		this._set({ sortColumn });
	}
	set sortDirection(sortDirection: SortDirection) {
		this._set({ sortDirection });
	}

  /**
   * Sets the state of the service
   * @param patch state to set
   */
	private _set(patch: Partial<State>) {
		Object.assign(this._state, patch);
		this._search$.next();
	}
  
  /**
   * Searches the articles for the specified term
   * @returns Observable of the search result
   */
  private _search(): Observable<SearchResult> {
    const { sortColumn, sortDirection, searchTerm } = this._state;

    // 1. Sort
    let articles = sort(this._articlesFromDb, sortColumn, sortDirection)

    // 2. Filter
    if(searchTerm)
      articles = articles.filter((article) => matches(article, searchTerm, this.pipe));

    return of({articles});
  }

  public pushArticle(articlePushObject: ArticlePushObject): Observable<any> {
    var postData = new DeployModel(articlePushObject.ArticleName, articlePushObject.environment, articlePushObject.systemName);
    console.log("pushArticle called", articlePushObject)
    return this.http.post(`${this.apiUrl}/api/Deployment/DeployArticleDetails`, postData);
  }
  pushSystem(systemName: string, environment: string): Observable<any> {
    var postData = new DeployModel("", environment, systemName);
    return this.http.post(`${this.apiUrl}/api/Deployment/DeploySystem`, postData);
  }
  deleteArticle(systemName: string, articleName: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/api/DXFReader/DeleteArticle/`+ systemName + "/" + articleName).pipe(tap(()=> {
     // this.loadArticles();
    }));
  }
  searchArticlesOrSystems(searchTerm: string): Observable<Article[]> {
    return this.http.get<Article[]>(`${this.apiUrl}/api/Search/SearchArticlesOrSystems/${searchTerm}`);
  }
  verifiedArticleByEngineer(articleVerifyObject: ArticleVerifyObject): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/Verification/VerifiedArticleByEngineer`, articleVerifyObject);
  }
  verifiedArticleByDesigner(articleVerifyObject: ArticleVerifyObject): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/Verification/VerifiedArticleByDesigner`, articleVerifyObject);
  }
  issuesIdentifiedByEngineer(articleVerifyObject: ArticleVerifyObject): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/Verification/ArticleIssuesIdentifiedByEngineer`, articleVerifyObject);
  }
  issuesIdentifiedByDesigner(articleVerifyObject: ArticleVerifyObject): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/Verification/ArticleIssuesIdentifiedByDesigner`, articleVerifyObject);
  }
  getFilteredArticles(filteredParams: any): Observable<any> {
    console.log('filteredParams', filteredParams);
    return this.http.post(`${this.apiUrl}/api/Search/FilterArticles`, filteredParams);
  }
  downloadArtifactsAsZip(imageList: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/FileManagement/DownloadArtifactsAsZip`, imageList, { responseType: 'blob' });
  }
  getPresignedURL(fileName: string, artifactType: string): Observable<string> {
  return this.http.get(
    `${this.apiUrl}/api/FileManagement/GeneratePreSignedURL/${fileName}/${artifactType}`,
    { responseType: 'text' }  //  tells Angular not to parse JSON
  );
}
 
}